// @rpg-unit/status-presets: status and buff presets (tier 3; composition framework, decision 0077). Six tuned effects
// on @rpg/abilities' effect model, cut from spike 002's `content/tables/effects.json`: burn, slow and stun (statuses
// moves apply) and healing over time, might and guard (buffs dishes and items give). Each modifies only standard stat
// ids (@rpg/contracts), so it means the same in any game whose stats are standard.
//
// A game takes the presets three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content` from
//     here) or add `STATUS_PRESET_CONTENT` to the tables it loads in the browser; the rows are the `effects` table;
//   - as @rpg/abilities definitions: `presetEffects()` or `toEffectDef(row)`;
//   - tuned by perceptual knobs: `tunePresets(rows, { potency, length })`.
// Names are string ids (`effect.burn`); the game's strings table carries them, and `STATUS_PRESET_STRINGS` holds the
// English defaults.
import type { EffectDef } from "@rpg/abilities";
import table from "./content/tables/effects.json";
import schema from "./content/schemas/effects.schema.json";
import strings from "./strings.json";

/** This unit's version (the manifest's and package.json's). */
export const STATUS_PRESETS_VERSION = "0.1.0" as const;

/** One preset, as its schema describes it (`src/content/schemas/effects.schema.json`). */
export interface StatusPreset {
  id: string;
  /** The display name: a reference into the game's strings table. */
  name: { table: string; id: string };
  kind: "status" | "buff";
  duration: "instant" | "timed" | "infinite";
  seconds?: number;
  period?: number;
  tickOnApply?: boolean;
  modifiers?: { attribute: string; op: "add" | "multiply" | "override"; value: number }[];
  grants?: string[];
  stacking?: { limit: number; by?: "target" | "source"; duration?: "refresh" | "keep" | "add"; expire?: "all" | "one" };
  /** Its icon's colour on the HUD and screens. */
  colour: string;
  /** Why it is tuned this way. */
  reason?: string;
}

/** The presets as a content table (`{ table: "effects", version, rows }`). */
export const STATUS_PRESETS_TABLE = table as { table: "effects"; version: number; rows: StatusPreset[] };

/** The table's row schema (JSON Schema, with @rpg/content's `x-ref` for the name). */
export const STATUS_PRESETS_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const STATUS_PRESET_CONTENT = {
  package: "@rpg-unit/status-presets",
  version: STATUS_PRESETS_VERSION,
  tables: [STATUS_PRESETS_TABLE],
  schemas: { effects: STATUS_PRESETS_SCHEMA },
} as const;

/** English defaults for the presets' names (`{ id, text }` rows for a strings table). */
export const STATUS_PRESET_STRINGS = strings as { id: string; text: string }[];

/** A preset as an @rpg/abilities effect definition (only the fields the effect model reads). */
export function toEffectDef(e: StatusPreset): EffectDef {
  return {
    id: e.id,
    duration: e.duration,
    ...(e.seconds !== undefined ? { seconds: e.seconds } : {}),
    ...(e.period !== undefined ? { period: e.period } : {}),
    ...(e.tickOnApply !== undefined ? { tickOnApply: e.tickOnApply } : {}),
    ...(e.modifiers ? { modifiers: e.modifiers } : {}),
    ...(e.grants ? { grants: e.grants } : {}),
    ...(e.stacking ? { stacking: e.stacking } : {}),
  };
}

/** Every preset as an effect definition, in table order. */
export function presetEffects(rows: readonly StatusPreset[] = STATUS_PRESETS_TABLE.rows): EffectDef[] {
  return rows.map(toEffectDef);
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const STATUS_PRESET_KNOBS = {
  potency: "stronger or weaker: scales every modifier's size (an add's amount; a multiply's distance from ×1); overrides are left alone. 1 is as tuned; 0.5 half as strong; 2 twice.",
  length: "longer or shorter: scales every timed effect's seconds (ticks keep their period, so a longer burn ticks more times). 1 is as tuned.",
} as const;

/** Knob values; each defaults to 1 (as tuned). */
export interface StatusKnobs {
  potency?: number;
  length?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** The presets turned by the knobs (new rows; the input is untouched). With no knobs, or all at 1, equal to the input. */
export function tunePresets(rows: readonly StatusPreset[], knobs: StatusKnobs = {}): StatusPreset[] {
  const potency = knobs.potency ?? 1;
  const length = knobs.length ?? 1;
  if (!(potency >= 0) || !(length > 0)) throw new Error(`status presets: potency must be ≥ 0 and length > 0 (got ${potency}, ${length})`);
  return rows.map((r) => {
    const out: StatusPreset = structuredClone(r);
    if (out.modifiers && potency !== 1) {
      out.modifiers = out.modifiers.map((m) => (m.op === "add" ? { ...m, value: round(m.value * potency) } : m.op === "multiply" ? { ...m, value: round(1 + (m.value - 1) * potency) } : m));
    }
    if (out.seconds !== undefined && length !== 1) out.seconds = round(out.seconds * length);
    return out;
  });
}
